import { useState } from 'react';
import type { CodeSample } from '../types';

const keywords = new Set(['void', 'int', 'float', 'double', 'char', 'uint8_t', 'uint16_t', 'uint32_t', 'volatile', 'const', 'static', 'if', 'else', 'for', 'while', 'switch', 'case', 'break', 'return', 'typedef', 'struct', 'enum', 'sizeof']);

function HighlightedLine({ line }: { line: string }) {
  const tokens = line.split(/(\/\*.*?\*\/|\/\/.*|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b0x[\dA-Fa-f]+(?:UL|U|L)?\b|\b\d+(?:\.\d+)?[fFuUlL]*\b|\b[A-Za-z_]\w*\b|#[A-Za-z]+)/g);
  return <>{tokens.map((token, index) => {
    let className = '';
    if (/^\/\*|^\/\//.test(token)) className = 'tok-comment';
    else if (/^['"]/.test(token)) className = 'tok-string';
    else if (/^(0x|\d)/.test(token)) className = 'tok-number';
    else if (/^#/.test(token)) className = 'tok-pre';
    else if (keywords.has(token)) className = 'tok-keyword';
    else if (/^(HAL_|__HAL_|RCC_|GPIO_|TIM_|ADC_|UART_|I2C_|SPI_|PWR_|SCB)/.test(token)) className = 'tok-hal';
    return <span key={`${index}-${token}`} className={className}>{token}</span>;
  })}</>;
}

export function CodeBlock({ sample }: { sample: CodeSample }) {
  const [view, setView] = useState<'HAL' | 'register'>('HAL');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(sample.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="code-card">
      <div className="code-toolbar">
        <div className="file-meta"><span className="file-dots"><i /><i /><i /></span><strong>{sample.filename}</strong><small>{sample.scope}</small></div>
        <button className="copy-button" type="button" onClick={copy}>{copied ? '✓ 已复制' : '复制代码'}</button>
      </div>
      <div className="code-tabs" role="tablist" aria-label="代码思路切换">
        <button role="tab" aria-selected={view === 'HAL'} className={view === 'HAL' ? 'active' : ''} onClick={() => setView('HAL')}>HAL 可运行代码</button>
        <button role="tab" aria-selected={view === 'register'} className={view === 'register' ? 'active' : ''} onClick={() => setView('register')}>LL / 寄存器思路</button>
      </div>
      {view === 'HAL' ? (
        <>
          <div className="assumption"><strong>配置假设</strong><span>{sample.assumptions}</span></div>
          <pre className="code-view" tabIndex={0} aria-label={`${sample.filename} C 语言代码`}><code>{sample.code.split('\n').map((line, index) => <span className="code-line" key={index}><i>{String(index + 1).padStart(2, '0')}</i><b><HighlightedLine line={line} /></b></span>)}</code></pre>
        </>
      ) : <div className="register-note"><span className="register-icon">REG</span><div><h4>HAL 之下发生了什么</h4><p>{sample.registerIdea}</p><p className="quiet">LL 和寄存器操作不是“更高级”的必经替代品；先用 HAL 建立正确配置，再在需要确定时序或性能的局部下沉。</p></div></div>}
      <div className="line-notes"><span className="section-kicker">关键行解释</span>{sample.explanations.map((note, index) => <div key={note}><i>{index + 1}</i><p>{note}</p></div>)}</div>
    </div>
  );
}
